import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  Download,
  Add,
  FilterList,
  MoreVert,
  Visibility,
  Delete,
  CheckCircle,
  Block,
  PlayArrow,
  Pause,
  Cancel,
  Inbox,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from './LoadingSpinner';

interface Column {
  field: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'datetime' | 'select';
  sortable?: boolean;
  searchable?: boolean;
  render?: 'status' | 'role' | 'currency' | 'badge' | 'percentage';
}

interface Filter {
  field: string;
  label: string;
  type: 'select' | 'text' | 'date';
  options?: string[];
}

interface BulkAction {
  id: string;
  label: string;
  icon: string;
  color?: 'error' | 'primary' | 'secondary';
}

interface RowAction {
  id: string;
  label: string;
  icon: string;
  color?: 'error' | 'primary' | 'secondary';
}

interface EntityMetadata {
  displayName: string;
  singularName: string;
  primaryKey: string;
  columns: Column[];
  filters: Filter[];
  bulkActions: BulkAction[];
  rowActions: RowAction[];
}

interface GenericEntityListProps {
  entityType: string;
  detailsRoute?: string; // e.g., '/bots' - will append /{id}
  onCreateClick?: () => void;
  createButtonLabel?: string;
  showCreateButton?: boolean;
  showExportButton?: boolean;
}

const GenericEntityList: React.FC<GenericEntityListProps> = ({
  entityType,
  detailsRoute,
  onCreateClick,
  createButtonLabel,
  showCreateButton = true,
  showExportButton = true,
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch entities
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['entities', entityType, page, rowsPerPage, search, filters],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rowsPerPage.toString(),
        search,
        ...filters,
      });
      return api.get(`/admin/entities/${entityType}?${params.toString()}`);
    },
    keepPreviousData: true,
  });

  const entities = data?.entities || [];
  const total = data?.total || 0;
  const metadata: EntityMetadata = data?.metadata || {
    displayName: entityType,
    singularName: entityType,
    primaryKey: 'id',
    columns: [],
    filters: [],
    bulkActions: [],
    rowActions: [],
  };

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (entityIds: string[]) =>
      api.post(`/admin/entities/${entityType}/bulk-delete`, { entityIds }),
    onSuccess: () => {
      queryClient.invalidateQueries(['entities', entityType]);
      setSelected([]);
      setSnackbar({ open: true, message: 'Entities deleted successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete entities', severity: 'error' });
    },
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ entityIds, operation }: { entityIds: string[]; operation: string }) =>
      api.post(`/admin/entities/${entityType}/bulk-update`, { entityIds, operation }),
    onSuccess: () => {
      queryClient.invalidateQueries(['entities', entityType]);
      setSelected([]);
      setSnackbar({ open: true, message: 'Entities updated successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update entities', severity: 'error' });
    },
  });

  // Handlers
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = entities.map((entity: any) => entity[metadata.primaryKey]);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleBulkAction = (actionId: string) => {
    setBulkActionAnchor(null);
    
    if (actionId === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selected.length} ${metadata.displayName.toLowerCase()}?`)) {
        bulkDeleteMutation.mutate(selected);
      }
    } else {
      bulkUpdateMutation.mutate({ entityIds: selected, operation: actionId });
    }
  };

  const handleRowAction = (actionId: string, entityId: string) => {
    if (actionId === 'view' && detailsRoute) {
      navigate(`${detailsRoute}/${entityId}`);
    } else if (actionId === 'delete') {
      if (window.confirm(`Are you sure you want to delete this ${metadata.singularName.toLowerCase()}?`)) {
        bulkDeleteMutation.mutate([entityId]);
      }
    } else if (actionId === 'toggle_status') {
      bulkUpdateMutation.mutate({ entityIds: [entityId], operation: 'toggle_status' });
    } else {
      bulkUpdateMutation.mutate({ entityIds: [entityId], operation: actionId });
    }
  };

  const renderCellValue = (entity: any, column: Column) => {
    const value = entity[column.field];

    if (value === null || value === undefined) {
      return '-';
    }

    switch (column.render) {
      case 'status':
        return (
          <Chip
            label={value ? 'Active' : 'Inactive'}
            color={value ? 'success' : 'default'}
            size="small"
          />
        );
      case 'role':
        return (
          <Chip
            label={value ? 'Admin' : 'User'}
            color={value ? 'secondary' : 'default'}
            size="small"
          />
        );
      case 'currency':
        return `$${Number(value).toFixed(2)}`;
      case 'badge':
        return <Chip label={value} size="small" />;
      case 'percentage':
        return `${Number(value).toFixed(2)}%`;
      default:
        if (column.type === 'datetime') {
          return new Date(value).toLocaleDateString();
        }
        if (column.type === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        return value;
    }
  };

  const getActionIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      Visibility,
      Delete,
      CheckCircle,
      Block,
      PlayArrow,
      Pause,
      Cancel,
    };
    const Icon = icons[iconName] || MoreVert;
    return <Icon />;
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {metadata.displayName} Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage {metadata.displayName.toLowerCase()}, filters, and bulk operations
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            size="small"
          >
            Refresh
          </Button>
          {showExportButton && (
            <Button variant="outlined" startIcon={<Download />} size="small">
              Export
            </Button>
          )}
          {showCreateButton && (
            <Button
              variant="contained"
              startIcon={<Add />}
              size="small"
              onClick={onCreateClick}
            >
              {createButtonLabel || `Add ${metadata.singularName}`}
            </Button>
          )}
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder={`Search ${metadata.displayName.toLowerCase()}...`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            size="small"
          >
            Filters
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Box display="flex" gap={2} mt={2} flexWrap="wrap">
            {metadata.filters.map((filter) => (
              <FormControl key={filter.field} size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{filter.label}</InputLabel>
                <Select
                  value={filters[filter.field] || 'all'}
                  onChange={(e) => handleFilterChange(filter.field, e.target.value)}
                  label={filter.label}
                >
                  {filter.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        </Collapse>
      </Paper>

      {/* Bulk Actions Bar */}
      {selected.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">
              {selected.length} {metadata.displayName.toLowerCase()} selected
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setBulkActionAnchor(e.currentTarget)}
            >
              Bulk Actions
            </Button>
            <Menu
              anchorEl={bulkActionAnchor}
              open={Boolean(bulkActionAnchor)}
              onClose={() => setBulkActionAnchor(null)}
            >
              {metadata.bulkActions.map((action) => (
                <MenuItem
                  key={action.id}
                  onClick={() => handleBulkAction(action.id)}
                  sx={{ color: action.color === 'error' ? 'error.main' : 'inherit' }}
                >
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < entities.length}
                    checked={entities.length > 0 && selected.length === entities.length}
                    onChange={handleSelectAllClick}
                    disabled={isLoading || !!error}
                  />
                </TableCell>
                {metadata.columns.map((column) => (
                  <TableCell key={column.field}>{column.label}</TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={metadata.columns.length + 2} align="center" sx={{ py: 8 }}>
                    <LoadingSpinner />
                  </TableCell>
                </TableRow>
              )}

              {error && (
                <TableRow>
                  <TableCell colSpan={metadata.columns.length + 2} align="center" sx={{ py: 8 }}>
                    <Alert severity="error">
                      Failed to load {metadata.displayName.toLowerCase()}. Please try again.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !error && entities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={metadata.columns.length + 2} align="center" sx={{ py: 8 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                      sx={{ py: 4 }}
                    >
                      <Inbox sx={{ fontSize: 64, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary">
                        No {metadata.displayName.toLowerCase()} found
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {search
                          ? `No results match your search "${search}"`
                          : `Get started by creating your first ${metadata.singularName.toLowerCase()}`}
                      </Typography>
                      {showCreateButton && onCreateClick && !search && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={onCreateClick}
                          sx={{ mt: 2 }}
                        >
                          {createButtonLabel || `Create ${metadata.singularName}`}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !error && entities.map((entity: any) => {
                const entityId = entity[metadata.primaryKey];
                const isItemSelected = isSelected(entityId);
                return (
                  <TableRow
                    hover
                    onClick={(event) => {
                      // If clicking on checkbox cell, just toggle selection
                      const target = event.target as HTMLElement;
                      if (target.closest('.MuiCheckbox-root') || target.closest('[role="checkbox"]')) {
                        handleClick(event, entityId);
                      } else if (detailsRoute) {
                        // Otherwise navigate to details page
                        navigate(`${detailsRoute}/${entityId}`);
                      }
                    }}
                    role="row"
                    tabIndex={-1}
                    key={entityId}
                    selected={isItemSelected}
                    sx={{ cursor: detailsRoute ? 'pointer' : 'default' }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, entityId)}
                      />
                    </TableCell>
                    {metadata.columns.map((column) => (
                      <TableCell key={column.field}>
                        {renderCellValue(entity, column)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {metadata.rowActions.map((action) => (
                          <Tooltip key={action.id} title={action.label}>
                            <IconButton
                              size="small"
                              color={action.color}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction(action.id, entityId);
                              }}
                            >
                              {getActionIcon(action.icon)}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenericEntityList;


